/**
 * usePledgeQueries — React Query wrappers for fetching sent and received pledges.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RewardPledge, ProofSubmission } from '@/types/rewards';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { PledgeState, PledgeQueries } from './types';
import { logger } from '@/utils/logger';

interface EnrichPledgeContext {
  sentPledges: RewardPledge[];
  receivedPledges: RewardPledge[];
  setSentPledges: (pledges: RewardPledge[]) => void;
  setReceivedPledges: (pledges: RewardPledge[]) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  toast: ReturnType<typeof useToast>;
}

export function usePledgeQueries(context: EnrichPledgeContext): PledgeQueries {
  const { 
    sentPledges,
    receivedPledges,
    setSentPledges,
    setReceivedPledges,
    setError,
    setLoading,
    toast
  } = context;
  const { user } = useAuth();

  const fetchPledges = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch sent pledges using new RPC helper
      const { data: sent, error: sentError } = await supabase
        .rpc('get_my_sent_pledges');
      
      if (sentError) throw sentError;
      
      // Fetch received pledges using new RPC helper
      const { data: received, error: receivedError } = await supabase
        .rpc('get_my_received_pledges');
      
      if (receivedError) throw receivedError;

      // Get user details for display names
      const userIds = new Set<string>();
      [...(sent || []), ...(received || [])].forEach(pledge => {
        if (pledge.from_user_id) userIds.add(pledge.from_user_id);
        if (pledge.to_user_id) userIds.add(pledge.to_user_id);
      });

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', Array.from(userIds));

      const userMap = new Map();
      profiles?.forEach(profile => {
        userMap.set(profile.id, profile.display_name || 'Unknown User');
      });

      // Fetch goal titles for context
      const goalIds = new Set<string>();
      [...(sent || []), ...(received || [])]
        .filter(pledge => pledge.goal_id)
        .forEach(pledge => {
          if (pledge.goal_id) goalIds.add(pledge.goal_id);
        });

      const { data: goals } = await supabase
        .from('goals')
        .select('id, title, progress, completed_at')
        .in('id', Array.from(goalIds));

      const goalMap = new Map();
      goals?.forEach(goal => {
        goalMap.set(goal.id, goal.title);
      });

      // Fetch todo texts for context
      const todoIds = new Set<string>();
      [...(sent || []), ...(received || [])]
        .filter(pledge => pledge.todo_id)
        .forEach(pledge => {
          if (pledge.todo_id) todoIds.add(pledge.todo_id);
        });

      const { data: todos } = await supabase
        .from('todos')
        .select('id, text')
        .in('id', Array.from(todoIds));

      const todoMap = new Map();
      todos?.forEach(todo => {
        todoMap.set(todo.id, todo.text);
      });

      // Enrich pledges with user names, goal/todo details, and goal progress
      const enrichPledges = (pledges: any[]) => {
        return pledges.map(pledge => {
          const goal = pledge.goal_id ? goals?.find(g => g.id === pledge.goal_id) : null;
          return {
            ...pledge,
            from_user_name: pledge.from_user_id ? userMap.get(pledge.from_user_id) : undefined,
            to_user_name: pledge.to_user_id ? userMap.get(pledge.to_user_id) : undefined,
            goal_title: pledge.goal_id ? goalMap.get(pledge.goal_id) : undefined,
            todo_text: pledge.todo_id ? todoMap.get(pledge.todo_id) : undefined,
            goal_progress: goal?.progress ?? undefined,
            goal_completed_at: goal?.completed_at ?? undefined,
          };
        });
      };

      // Enrich both arrays with proof data
      const enrichWithProofs = async (pledges: any[]): Promise<RewardPledge[]> => {
        if (pledges.length === 0) return pledges;

        try {
          const pledgeIds = pledges.map(p => p.id);
          
          // Fetch all proof submissions for these pledges
          const { data: proofs, error } = await supabase
            .from('proof_submissions')
            .select('*')
            .in('pledge_id', pledgeIds)
            .order('created_at', { ascending: false });

          if (error) throw error;

          // Build a map of pledge_id -> proof data
          const proofMap = new Map<string, ProofSubmission[]>();
          proofs?.forEach(proof => {
            if (!proofMap.has(proof.pledge_id)) {
              proofMap.set(proof.pledge_id, []);
            }
            // Convert Json types to proper arrays for ProofSubmission
            const convertedProof: ProofSubmission = {
              ...proof,
              image_urls: Array.isArray(proof.image_urls) 
                ? proof.image_urls.filter(url => typeof url === 'string') as string[]
                : [],
              link_urls: Array.isArray(proof.link_urls) 
                ? proof.link_urls.filter(url => typeof url === 'string') as string[]
                : []
            };
            proofMap.get(proof.pledge_id)!.push(convertedProof);
          });

          // Enrich pledges with proof information
          return pledges.map(pledge => {
            const pledgeProofs = proofMap.get(pledge.id) || [];
            const pendingProofs = pledgeProofs.filter(p => p.status === 'pending');
            const latestProof = pledgeProofs[0]; // Already sorted by created_at desc

            return {
              ...pledge,
              hasPendingProof: pendingProofs.length > 0,
              hasAnyProof: pledgeProofs.length > 0,
              lastProofAt: latestProof?.created_at,
            };
          });
        } catch (error) {
          logger.error('Error enriching pledges with proof data:', error);
          return pledges; // Return original pledges if enrichment fails
        }
      };

      const enrichedSent = enrichPledges(sent || []);
      const enrichedReceived = enrichPledges(received || []);

      // Add proof information
      const finalSent = await enrichWithProofs(enrichedSent);
      const finalReceived = await enrichWithProofs(enrichedReceived);

      setSentPledges(finalSent);
      setReceivedPledges(finalReceived);
      
      logger.debug('Fetched pledges:', { sent: enrichPledges(sent || []), received: enrichPledges(received || []) });
    } catch (error: any) {
      // Silent fail — pledges are a background fetch; surface errors in the
      // data layer only so transient blips don't pop a toast across the app.
      logger.error('Error fetching pledges:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getProofsForPledge = useCallback(async (pledgeId: string): Promise<ProofSubmission[]> => {
    if (!user) return [];
    
    try {
      const { data, error } = await supabase
        .rpc('get_proofs_for_pledge', {
          pledge_id_param: pledgeId,
          user_id_param: user.id
        });
      
      if (error) throw error;
      
      // Normalize Json fields to string arrays to ensure UI renders images/links
      const normalize = (val: unknown): string[] => {
        if (Array.isArray(val)) return val.filter((v) => typeof v === 'string') as string[];
        if (typeof val === 'string') {
          try {
            const parsed = JSON.parse(val);
            return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
          } catch {
            return [];
          }
        }
        return [];
      };
      
      return (data || []).map((p: any) => ({
        ...p,
        image_urls: normalize(p.image_urls),
        link_urls: normalize(p.link_urls),
      })) as ProofSubmission[];
    } catch (error: any) {
      logger.error('Error fetching proofs:', error);
      return [];
    }
  }, [user]);

  return {
    fetchPledges,
    getProofsForPledge
  };
}
